import {
  addDays,
  addMonths,
  isBefore,
  lastDayOfMonth,
  startOfDay,
  startOfMonth,
} from '@repo/sdk/date'
import { z } from '@repo/schema'

import type { DateFilterValue, DateWindow, FilterModel, RelativeDate } from '../core/types'

import { type Locale, t } from './i18n'

export const RelativeDateSchema = z.object({
  days: z.int(),
  edge: z.enum(['end', 'start']).optional(),
  months: z.int().optional(),
})

/** The furthest a typed offset reaches either side of today: ten years keeps it a valid date. */
export const MAX_DAY_OFFSET = 3650

/** The furthest a window reaches in months: ten years, as in days. */
export const MAX_MONTH_OFFSET = 120

const RelativeDaysInputSchema = z.coerce.number().int().min(1).max(MAX_DAY_OFFSET)

/** Reads the typed number of days; anything but a whole number from 1 to 3650 is undefined. */
export const parseRelativeDays = (text: string) => {
  const parsed = RelativeDaysInputSchema.safeParse(text)
  return parsed.success ? parsed.data : undefined
}

// A blank field is no number rather than 0, which coercion would make it.
const DayOffsetInputSchema = z
  .string()
  .trim()
  .min(1)
  .pipe(z.coerce.number<string>().int().min(-MAX_DAY_OFFSET).max(MAX_DAY_OFFSET))

/** Reads a typed day offset from today; anything but a whole number from -3650 to 3650 is undefined. */
export const parseDayOffset = (text: string) => {
  const parsed = DayOffsetInputSchema.safeParse(text)
  return parsed.success ? parsed.data : undefined
}

export const isRelativeDate = (value: DateFilterValue): value is RelativeDate =>
  RelativeDateSchema.safeParse(value).success

export const isAbsoluteDate = (value: DateFilterValue): value is Date => !isRelativeDate(value)

/** Reads a typed count of days or months before or after today, from 0 to ten years. */
export const parseWindowCount = (text: string, unit: DateWindow['unit']) => {
  const count = parseDayOffset(text)
  const max = unit === 'days' ? MAX_DAY_OFFSET : MAX_MONTH_OFFSET
  return count === undefined || count < 0 || count > max ? undefined : count
}

/** A relative value counted in days from today alone, with no month anchor. */
export const isDayOffset = (value: DateFilterValue): value is RelativeDate =>
  isRelativeDate(value) && value.months === undefined

/** A relative value whole months from today, on the same day of the month. */
const isMonthOffset = (value: RelativeDate) =>
  value.months !== undefined && value.edge === undefined && value.days === 0

/** The day a relative value counts from: today, a day months from it, or a month's first or last day. */
const anchorDay = ({ edge, months }: RelativeDate, today: Date) => {
  if (months === undefined) return startOfDay(today)
  if (edge === undefined) return addMonths(startOfDay(today), months)
  const month = addMonths(startOfMonth(today), months)
  return edge === 'end' ? lastDayOfMonth(month) : month
}

/** Relative values become a date on the user's local calendar; absolute values pass through. */
export const resolveDateFilterValue = (value: DateFilterValue, today = new Date()) =>
  isRelativeDate(value) ? addDays(anchorDay(value, today), value.days) : value

/** Resolves every value and orders a range, whose endpoints can swap as the days roll on. */
export const resolveDateFilterValues = (values: DateFilterValue[], today = new Date()) => {
  const dates = values.map(value => resolveDateFilterValue(value, today))

  return dates.length === 2 && isBefore(dates[1], dates[0]) ? [dates[1], dates[0]] : dates
}

const relativeDayLabel = (days: number, locale: Locale) => {
  if (days === 0) return t('filters.date.relative.today', locale)
  if (days === 1) return t('filters.date.relative.tomorrow', locale)
  if (days === -1) return t('filters.date.relative.yesterday', locale)

  const key = days > 0 ? 'filters.date.relative.inDays' : 'filters.date.relative.daysAgo'
  return t(key, locale).replace('{n}', String(Math.abs(days)))
}

const dayCountLabel = (days: number, locale: Locale) =>
  days === 1
    ? t('filters.date.relative.oneDay', locale)
    : t('filters.date.relative.dayCount', locale).replace('{n}', String(days))

export const relativeDateLabel = (values: RelativeDate[], locale: Locale) => {
  const [from, to] = values
  if (!to) return relativeDayLabel(from.days, locale)
  if (from.days === 0 && to.days > 1) {
    return t('filters.date.relative.nextDays', locale).replace('{n}', String(to.days))
  }
  if (to.days === 0 && from.days < -1) {
    return t('filters.date.relative.lastDays', locale).replace('{n}', String(-from.days))
  }
  if (from.days < 0 && to.days > 0) {
    return t('filters.date.relative.window', locale)
      .replace('{before}', dayCountLabel(-from.days, locale))
      .replace('{after}', dayCountLabel(to.days, locale))
  }
  return `${relativeDayLabel(from.days, locale)} - ${relativeDayLabel(to.days, locale)}`
}

/** A window from `before` days or months ago to `after` from now; nothing either side is today. */
export const windowAroundToday = ({ after, before, unit }: DateWindow): RelativeDate[] => {
  const at = (count: number): RelativeDate =>
    unit === 'days' ? { days: count } : { days: 0, months: count }
  // 0 - n, unlike -n, keeps nothing before today a plain 0 rather than -0.
  return before === 0 && after === 0 ? [at(0)] : [at(0 - before), at(after)]
}

const windowUnit = (values: RelativeDate[]): DateWindow['unit'] | undefined => {
  if (values.every(value => value.months === undefined)) return 'days'
  if (values.every(isMonthOffset)) return 'months'
}

/** The window around today the values are, or undefined when they are not one. */
export const readWindow = (values: RelativeDate[]): DateWindow | undefined => {
  const [from, to = from] = values
  const unit = windowUnit(values)
  if (!from || values.length > 2 || !unit) return
  const count = (value: RelativeDate) => (unit === 'days' ? value.days : (value.months ?? 0))
  const window = { after: count(to), before: 0 - count(from), unit }
  return window.before >= 0 && window.after >= 0 ? window : undefined
}

const monthCountLabel = (months: number, locale: Locale) =>
  months === 1
    ? t('filters.date.relative.oneMonth', locale)
    : t('filters.date.relative.monthCount', locale).replace('{n}', String(months))

/** Names a window of whole months around today, or undefined when the values are not one. */
export const monthWindowLabel = (values: RelativeDate[], locale: Locale) => {
  const window = readWindow(values)
  if (window?.unit !== 'months') return
  const { after, before } = window
  if (before === 0 && after === 0) return t('filters.date.relative.today', locale)
  const span = (key: string, months: number) =>
    t(key, locale).replace('{span}', monthCountLabel(months, locale))
  if (before === 0) return span('filters.date.relative.nextSpan', after)
  if (after === 0) return span('filters.date.relative.lastSpan', before)
  return t('filters.date.relative.window', locale)
    .replace('{before}', monthCountLabel(before, locale))
    .replace('{after}', monthCountLabel(after, locale))
}

/** The rolling choices offered for a typed number of days. */
export const relativeDateChoices = (days: number): RelativeDate[][] => [
  [{ days: 0 }],
  [{ days: 1 }],
  // "In 1 day" is tomorrow, which is already offered.
  ...(days > 1 ? [[{ days }]] : []),
  [{ days: -days }],
  [{ days: 0 }, { days }],
  [{ days: -days }, { days: 0 }],
]

/** Last, this and next month: each choice's offset from this month, with its label. */
export const MONTH_CHOICES = [
  { label: 'filters.date.relative.lastMonth', months: -1 },
  { label: 'filters.date.relative.thisMonth', months: 0 },
  { label: 'filters.date.relative.nextMonth', months: 1 },
]

/** A whole month, from its first day to its last, that many months from this one. */
export const monthChoice = (months: number): RelativeDate[] => [
  { days: 0, edge: 'start', months },
  { days: 0, edge: 'end', months },
]

/** The month choice the values are, or undefined when they are not one. */
export const findMonthChoice = ([from, to, ...rest]: RelativeDate[]) => {
  if (!from || !to || rest.length > 0 || from.months === undefined) return
  const whole = from.edge === 'start' && to.edge === 'end' && to.months === from.months
  if (!whole || from.days !== 0 || to.days !== 0) return
  return MONTH_CHOICES.find(choice => choice.months === from.months)
}

const sameDays = (a: RelativeDate[], b: RelativeDate[]) =>
  a.length === b.length && a.every((value, index) => value.days === b[index].days)

/** Whether the values are one of the rolling or month choices rather than a typed day range. */
export const isRelativeDateChoice = (values: RelativeDate[]) => {
  if (!values.every(isDayOffset)) return findMonthChoice(values) !== undefined
  const days = Math.max(1, ...values.map(value => Math.abs(value.days)))
  return relativeDateChoices(days).some(choice => sameDays(choice, values))
}

/** A typed day range in the column's own counts: "-10 to 5". */
export const dayRangeLabel = ([from, to = from]: RelativeDate[], locale: Locale) =>
  t('filters.date.dayRange', locale)
    .replace('{from}', String(from.days))
    .replace('{to}', String(to.days))

/** "More than N days overdue" is due before N days ago; N = 0 is plain "overdue". */
export const overdueChoice = (days: number) => ({
  operator: 'is before' as const,
  values: [{ days: -days || 0 }],
})

/**
 * How many days an overdue choice is past due, or undefined when the filter is not one. Only on a
 * column offering overdue choices does "is before" today, or a day before it, read as overdue.
 */
export const overdueDays = (column: { overdueChoices?: boolean }, filter?: FilterModel) => {
  if (!column.overdueChoices || filter?.type !== 'date' || filter.operator !== 'is before') return
  const [value, ...rest] = filter.values
  if (rest.length > 0 || !isDayOffset(value) || value.days > 0) return
  return -value.days
}

export const overdueLabel = (days: number, locale: Locale) => {
  if (days === 0) return t('filters.date.relative.overdue', locale)
  if (days === 1) return t('filters.date.relative.overdueDay', locale)
  return t('filters.date.relative.overdueDays', locale).replace('{n}', String(days))
}
